import { z } from 'zod';
import { apiClient } from '#/api/client';
import { MessageTemplateSchema } from '#/components/ui/messages/model/messageTemplate';
import {
  MESSAGE_TEMPLATE_ENDPOINT_BASE,
  MESSAGE_QUERY_KEY_NAMESPACE,
} from './utils';
import { useQuery } from '@tanstack/react-query';

const PagingSchema = z.object({
  currentPage: z.number(),
  totalPages: z.number(),
  totalCount: z.number(),
  pageSize: z.number(),
});

export const MessageTemplatesFilterSchema = z.object({
  title: MessageTemplateSchema.shape.title.optional(),
  state: MessageTemplateSchema.shape.state.array().catch([]),
  category: MessageTemplateSchema.shape.messageCategoryKey.optional(),
});

export type MessageTemplatesFilterParams = z.infer<
  typeof MessageTemplatesFilterSchema
>;

type MessageTemplatesPagingParams = {
  page: number;
  pageSize: number;
};

export type MessageTemplatesParams = MessageTemplatesPagingParams &
  MessageTemplatesFilterParams;

const MessageTemplatesResponseSchema = z.object({
  items: z.array(MessageTemplateSchema),
  paging: PagingSchema,
  filterParams: MessageTemplatesFilterSchema,
});

type MessageTemplatesResponse = z.infer<typeof MessageTemplatesResponseSchema>;

async function fetchMessageTemplateList(params: MessageTemplatesParams) {
  const response = await apiClient.get<{ data: MessageTemplatesResponse }>(
    `${MESSAGE_TEMPLATE_ENDPOINT_BASE}`,
    {
      params,
      paramsSerializer(params) {
        const cleaned = Object.fromEntries(
          Object.entries(params).filter(([, value]) =>
            Array.isArray(value) ? value.length > 0 : value !== undefined,
          ),
        );

        const urlParams = new URLSearchParams(cleaned);
        return urlParams.toString();
      },
    },
  );

  return response.data.data;
}

export function useMessageTemplateList(
  params: MessageTemplatesFilterParams,
  paging: MessageTemplatesPagingParams,
) {
  return useQuery({
    queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplates', params, paging],
    queryFn: async () => {
      try {
        const response = await fetchMessageTemplateList({
          ...params,
          ...paging,
        });

        const items = response.items
          .map((item) => {
            const { success, data, error } = MessageTemplateSchema.safeParse(
              item,
              {
                reportInput: true,
              },
            );

            if (success) {
              return data;
            }

            console.warn(error);
            return undefined;
          })
          .filter((v) => !!v);

        const pagingData = MessageTemplatesResponseSchema.shape.paging.parse(
          response.paging,
        );
        const filterParams =
          MessageTemplatesResponseSchema.shape.filterParams.parse(
            response.filterParams,
          );

        return {
          items,
          paging: pagingData,
          filterParams,
        };
      } catch (err) {
        console.error(err);

        throw err;
      }
    },
    placeholderData: (prev) => prev,
  });
}
